import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  "Albania","Algeria","Argentina","Armenia","Australia","Austria","Azerbaijan","Bahamas","Bahrain",
  "Bangladesh","Barbados","Belgium","Bhutan","Bosnia and Herzegovina","Brazil","Brunei","Bulgaria",
  "Cambodia","Cameroon","Canada","Chile","China","Colombia","Costa Rica","Croatia","Cuba","Cyprus",
  "Czech Republic","Denmark","Dominican Republic","Ecuador","Egypt","Estonia","Ethiopia","Fiji",
  "Finland","France","Georgia","Germany","Ghana","Greece","Hong Kong","Hungary","Iceland","India",
  "Indonesia","Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan",
  "Kenya","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Libya","Lithuania","Luxembourg","Macau",
  "Madagascar","Malaysia","Maldives","Malta","Mauritius","Mexico","Mongolia","Morocco","Mozambique",
  "Myanmar","Nepal","Netherlands","New Zealand","Nigeria","North Macedonia","Norway","Oman","Pakistan",
  "Panama","Peru","Philippines","Poland","Portugal","Qatar","Romania","Saudi Arabia","Senegal",
  "Serbia","Seychelles","Singapore","Slovakia","Slovenia","South Africa","South Korea","Spain",
  "Sri Lanka","Sudan","Sweden","Switzerland","Taiwan","Tajikistan","Tanzania","Thailand",
  "Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Uganda","United Arab Emirates",
  "United Kingdom","United States","Uruguay","Uzbekistan","Venezuela","Vietnam","Zambia","Zimbabwe","Other"
];

/**
 * HotelConfirmation — auto-verifies the entered hotel against Google Business
 * Profile, updates name/location to Google's authoritative version, and hard-
 * rejects if GBP category isn't hotel-like (surfaces Modal C via onGbpRejected).
 *
 * Props:
 *   extractedData  — { hotelName, city, country } pulled from the URL metadata step
 *   onConfirm      — called with { hotelName, city, country } when user confirms
 *   onBack         — unused here but kept for future back-navigation wiring
 *   onGbpRejected  — called with { errorType, errorMsg } when GBP category
 *                    is non-hotel; parent shows the Modal C overlay.
 */
export default function HotelConfirmation({ extractedData, onConfirm, onBack, onGbpRejected }) {
  const [isVerifying, setIsVerifying] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [hotelName, setHotelName] = useState(extractedData?.hotelName || '');
  const [city, setCity] = useState(extractedData?.city || '');
  const [country, setCountry] = useState(extractedData?.country || '');
  // gbpFailed: surface the "Proceed anyway" escape hatch only after a real
  // verification attempt came back without a usable GBP match (or errored).
  // Without this, users hit by a transient DataForSEO GBP timeout were trapped
  // on the edit screen — clicking Confirm re-ran the same recheck and bounced
  // them back to the same edit screen indefinitely. The pipeline's own GBP
  // check is the real backstop, so we let the user proceed past a stuck
  // recheck — the audit will fail-fast cleanly if the hotel truly has no GBP.
  const [gbpFailed, setGbpFailed] = useState(false);

  const canStart = hotelName.trim() && city.trim() && country.trim();

  const verifyGbp = async () => {
    if (!hotelName.trim() || !city.trim() || !country.trim()) {
      setIsVerifying(false);
      setShowEdit(true);
      return;
    }
    setIsVerifying(true);
    try {
      const response = await fetch(`${API_URL}/api/recheck-gbp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hotelName, city, country }),
      });
      const data = await response.json();

      if (!data.success || !data.gbpFound) {
        // GBP missing — user can edit/retry; pipeline GBP check is the backstop.
        setShowEdit(true);
        setGbpFailed(true);
        return;
      }

      if (!data.isHotelCategory) {
        // GBP found but non-hotel category → hard reject, surface Modal C.
        onGbpRejected?.({
          errorType: 'non_hotel_gbp',
          errorMsg: `Google Business Profile lists this as "${data.gbpCategory}" — not a hotel category. Update the primary category to 'Hotel' or similar at business.google.com, then re-run the audit.`,
        });
        return;
      }

      // GBP verified as a hotel — auto-update fields to match Google's record.
      if (data.matchedName && !data.matchedName.startsWith('http')) {
        setHotelName(data.matchedName);
      }
      if (data.gbpCity && !city.trim()) setCity(data.gbpCity);
      if (data.gbpCountry && !country.trim()) setCountry(data.gbpCountry);
      setShowEdit(false);
      setGbpFailed(false);
    } catch (err) {
      console.error('GBP verification failed:', err);
      setShowEdit(true);
      setGbpFailed(true);
    } finally {
      setIsVerifying(false);
    }
  };

  const proceedAnyway = () => {
    // Skip the GBP recheck — the audit pipeline's GBP step is the backstop and
    // will surface a friendly error if the hotel genuinely has no Google
    // Business Profile.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'hotel_confirmed', country, gbp_bypass: true });
    } catch (_) {}
    onConfirm({ hotelName, city, country });
  };

  useEffect(() => {
    verifyGbp();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const locationText = [city, country].filter(Boolean).join(', ') || 'Location not specified';

  const fieldStyle = (value, required) => ({
    width: '100%', boxSizing: 'border-box', background: '#fff',
    border: `1px solid ${required && !value.trim() ? '#DC2626' : 'rgba(10,10,26,0.12)'}`,
    borderRadius: 10, padding: '12px 16px',
    fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: '#0A0A1A', outline: 'none',
  });
  const labelStyle = {
    fontFamily: "'DM Sans',sans-serif", fontSize: '11.5px', fontWeight: 600,
    color: 'rgba(10,10,26,0.65)', marginBottom: 6, display: 'block',
  };

  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'calc(100vh - 60px)',padding:'40px 20px',background:'var(--bg)'}}>
      <div style={{maxWidth:480,width:'100%',background:'#fff',border:'1px solid rgba(10,10,26,0.08)',borderRadius:20,padding:'40px 36px',boxShadow:'0 4px 6px rgba(0,0,0,0.03),0 20px 60px rgba(0,0,0,0.07)',position:'relative'}}>
        <div style={{position:'absolute',top:0,left:24,right:24,height:2,background:'linear-gradient(90deg,#2D4DE0,#4A6FE3,rgba(45,77,224,0))'}} />
        <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:800,color:'#0A0A1A',textAlign:'center',marginBottom:8}}>
          {isVerifying
            ? 'Finding your hotel on Google...'
            : showEdit
              ? 'Confirm your hotel details'
              : 'Is this how your hotel appears on Google?'}
        </h2>
        <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:'13.5px',color:'rgba(10,10,26,0.65)',textAlign:'center',marginBottom:32,lineHeight:1.6}}>
          {isVerifying
            ? 'Verifying your Google Business Profile...'
            : showEdit
              ? 'Make sure these match your Google listing. We use them to find your hotel across AI platforms.'
              : 'This name will be used to find your hotel on Google. Make sure it matches your Google listing exactly.'}
        </p>

        {isVerifying ? (
          <div style={{display:'flex',justifyContent:'center',padding:'32px 0'}}>
            <div className="spinner" style={{width:32,height:32,borderWidth:3,borderColor:'rgba(45,77,224,0.15)',borderTopColor:'#2D4DE0'}} />
          </div>
        ) : showEdit ? (
          <>
            {gbpFailed && (
              <div
                data-testid="gbp-failed-notice"
                style={{
                  background:'#F0F1F7', border:'1px solid rgba(10,10,26,0.08)',
                  borderRadius:10, padding:'12px 14px', marginBottom:16,
                  fontFamily:"'DM Sans',sans-serif", fontSize:'12.5px',
                  color:'rgba(10,10,26,0.72)', lineHeight:1.55,
                }}
              >
                We couldn't auto-verify this on Google's listings just now.
                Confirm the details below — or proceed anyway and we'll
                continue without the Google verification step.
              </div>
            )}
            <div style={{marginBottom:16}}>
              <label style={labelStyle}>Hotel name <span style={{color:'#DC2626'}}>*</span></label>
              <input
                type="text"
                value={hotelName}
                onChange={e => setHotelName(e.target.value)}
                placeholder="e.g. The Imperial New Delhi"
                style={fieldStyle(hotelName, true)}
                data-testid="hotel-name-input"
              />
            </div>
            <div style={{display:'flex',gap:12,marginBottom:16}}>
              <div style={{flex:1}}>
                <label style={labelStyle}>City <span style={{color:'#DC2626'}}>*</span></label>
                <input
                  type="text"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  placeholder="e.g. New Delhi"
                  style={fieldStyle(city, true)}
                  data-testid="city-input"
                />
              </div>
              <div style={{flex:1}}>
                <label style={labelStyle}>Country <span style={{color:'#DC2626'}}>*</span></label>
                <select
                  value={country}
                  onChange={e => setCountry(e.target.value)}
                  style={fieldStyle(country, true)}
                  data-testid="country-input"
                >
                  <option value="">Select country</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <button
              disabled={!canStart}
              onClick={verifyGbp}
              data-testid="confirm-details-btn"
              style={{
                width:'100%',
                background: canStart ? '#2D4DE0' : 'rgba(45,77,224,0.3)',
                border:'none', borderRadius:10, padding:16,
                fontFamily:"'Outfit',sans-serif", fontSize:16, fontWeight:700, color:'#fff',
                cursor: canStart ? 'pointer' : 'not-allowed',
                boxShadow: canStart ? '0 8px 24px rgba(45,77,224,0.28)' : 'none',
              }}
            >
              Confirm
            </button>
            {gbpFailed && (
              <button
                disabled={!canStart}
                onClick={proceedAnyway}
                data-testid="proceed-anyway-btn"
                style={{
                  width:'100%', marginTop:12,
                  background:'transparent',
                  border:'1px solid rgba(10,10,26,0.15)', borderRadius:10, padding:14,
                  fontFamily:"'Outfit',sans-serif", fontSize:14, fontWeight:600,
                  color: canStart ? '#0A0F2E' : 'rgba(10,10,26,0.35)',
                  cursor: canStart ? 'pointer' : 'not-allowed',
                }}
              >
                Proceed without Google verification
              </button>
            )}
          </>
        ) : (
          <>
            <div style={{background:'#F8F9FF',border:'1px solid rgba(45,77,224,0.15)',borderRadius:14,padding:24,marginBottom:16}}>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:700,color:'#0A0A1A',marginBottom:8}}>{hotelName}</div>
              <div style={{display:'flex',alignItems:'center',gap:6,fontFamily:"'DM Sans',sans-serif",fontSize:14,color:'rgba(10,10,26,0.65)'}}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#2D4DE0" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 1C4.79 1 3 2.79 3 5c0 3 4 8 4 8s4-5 4-8c0-2.21-1.79-4-4-4z"/>
                  <circle cx="7" cy="5" r="1.5"/>
                </svg>
                {locationText}
              </div>
            </div>
            <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,color:'rgba(10,10,26,0.62)',textAlign:'center',marginBottom:24}}>
              Not right?{' '}
              <span
                onClick={() => setShowEdit(true)}
                data-testid="edit-details-btn"
                style={{color:'#2D4DE0',cursor:'pointer',textDecoration:'underline'}}
              >
                Edit details
              </span>
            </p>
            <button
              onClick={() => {
                // GTM — user confirmed the hotel on the GBP verification screen.
                // Sits between audit_started (form submit) and audit_submit (API OK).
                try {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({ event: 'hotel_confirmed', country });
                } catch (_) {}
                onConfirm({ hotelName, city, country });
              }}
              data-testid="start-audit-btn"
              style={{
                width:'100%', background:'#2D4DE0', border:'none', borderRadius:10, padding:16,
                fontFamily:"'Outfit',sans-serif", fontSize:16, fontWeight:700, color:'#fff',
                cursor:'pointer', boxShadow:'0 8px 24px rgba(45,77,224,0.28)',
              }}
            >
              Yes, Start My Audit
            </button>
          </>
        )}
      </div>
    </div>
  );
}
